/**
 * Football 101 catalog (docs/TUTORIAL.md §5): the lessons as the menus see them (number, title, goal, camp), and the
 * view types the tutorial engine (src/app/modes/tutorial) publishes for the HUD. Pure data: the engine imports it,
 * the hub / HOW TO PLAY / coach panel render it.
 */
import type { ButtonName, CharacterId } from '@hf/shared';

export type LessonId =
  | 'move'
  | 'snap'
  | 'run'
  | 'downs'
  | 'pass'
  | 'defense'
  | 'counters'
  | 'fakes'
  | 'meters'
  | 'special'
  | 'scoring'
  | 'scrimmage';

export type TutorialCamp = 'rookie' | 'pro' | 'gameday';

/** HUD parts a lesson shows (progressive reveal). */
export type HudPart =
  'meters' | 'stamina' | 'power' | 'playclock' | 'rush' | 'chyron' | 'scorebug' | 'captions' | 'moments';

/**
 * How a keycap prompt is pressed (rendered with distinct notations, §3.8). `wait` = no key: the bubble says to hold
 * still (a defender before GO: the bubble used to say DIVE while the field said WAIT FOR GO). `release` = LET GO of
 * the held key now (a charge in its green band: QA round 3 — the bubble said HOLD for the whole charge).
 */
export type KeyMode = 'tap' | 'hold' | 'double' | 'chord' | 'then' | 'holdWhile' | 'wait' | 'release';

export interface KeyPrompt {
  buttons: ButtonName[];
  mode: KeyMode;
  /** Word under the caps: 'RUN', 'JUMP'… */
  verb: string;
}

/** The world bubble over the player's head: the one button to press NOW. */
export interface NowPrompt {
  button: ButtonName;
  mode: KeyMode;
  verb: string;
  /** Second button of a `then` / `chord` / `holdWhile` prompt. */
  then?: ButtonName;
  /**
   * `late`: a `release` past the green band: still LET GO, in red (too late already). `ready`: the key that comes
   * NEXT, not yet (a GET READY ribbon, a calm blue bubble, no tap pulse): lesson 6's leap warns before his jump.
   */
  tone?: 'late' | 'ready';
}

/**
 * The opponent's tell: a glyph above his head before he commits, named with the counter square's own words (the
 * move he is about to make): DIVE ⬇ and LEAP ⬆ for a defender, TRUCK ▼ and HURDLE ⬆ for a carrier.
 */
export type TeachCue = 'dive' | 'leap' | 'truck' | 'hurdle';

export interface LessonInfo {
  id: LessonId;
  number: number;
  title: string;
  /** One plain sentence (title slate, hub focus card). */
  goal: string;
  camp: TutorialCamp;
  /** Rough length, seconds. */
  seconds: number;
}

export const LESSONS: readonly LessonInfo[] = [
  {
    id: 'move',
    number: 1,
    title: 'MOVE & HOP',
    goal: 'Move your head around the field.',
    camp: 'rookie',
    seconds: 25,
  },
  {
    id: 'snap',
    number: 2,
    title: 'THE SNAP',
    goal: 'Every play starts with a snap at the blue line.',
    camp: 'rookie',
    seconds: 25,
  },
  {
    id: 'run',
    number: 3,
    title: 'RUN & GET TACKLED',
    goal: 'Carry the ball as far as you can. A tackle ends the play.',
    camp: 'rookie',
    seconds: 35,
  },
  {
    id: 'downs',
    number: 4,
    title: 'DOWNS & TOUCHDOWNS',
    goal: '4 tries to gain 10 yards, all the way to the END ZONE.',
    camp: 'rookie',
    seconds: 75,
  },
  {
    id: 'pass',
    number: 5,
    title: 'THROW & CHASE',
    goal: 'Throw the ball forward, then catch it yourself.',
    camp: 'rookie',
    seconds: 60,
  },
  {
    id: 'defense',
    number: 6,
    title: 'DEFENSE',
    goal: 'Stop the ball carrier: wait for GO, then tackle him.',
    camp: 'pro',
    seconds: 55,
  },
  {
    id: 'counters',
    number: 7,
    title: 'THE COUNTER SQUARE',
    goal: 'Every tackle has an answer. Learn what beats what.',
    camp: 'pro',
    seconds: 60,
  },
  {
    id: 'fakes',
    number: 8,
    title: 'FAKES',
    goal: 'Make him commit to the wrong move.',
    camp: 'pro',
    seconds: 60,
  },
  {
    id: 'meters',
    number: 9,
    title: 'STAMINA & POWER',
    goal: 'Fakes cost stamina. Big plays fill power.',
    camp: 'pro',
    seconds: 40,
  },
  {
    id: 'special',
    number: 10,
    title: 'YOUR SPECIAL',
    goal: "Full power? Press Special for your head's signature move.",
    camp: 'pro',
    seconds: 30,
  },
  {
    id: 'scoring',
    number: 11,
    title: 'SCORING',
    goal: 'Score: touchdowns, the extra point and field goals.',
    camp: 'pro',
    seconds: 60,
  },
  {
    id: 'scrimmage',
    number: 12,
    title: 'SCRIMMAGE',
    goal: 'Play a real drive each against the CPU.',
    camp: 'gameday',
    seconds: 90,
  },
];

export const CAMPS: readonly { id: TutorialCamp; title: string; sub: string }[] = [
  { id: 'rookie', title: 'ROOKIE CAMP', sub: '4 MIN · THE BASICS' },
  { id: 'pro', title: 'PRO CAMP', sub: '5 MIN · READS, FAKES & SCORING' },
  { id: 'gameday', title: 'GAME DAY', sub: '2 MIN · YOUR FIRST DRIVE' },
];

export const lessonInfo = (id: LessonId): LessonInfo => LESSONS.find((l) => l.id === id) ?? LESSONS[0]!;
export const lessonByNumber = (n: number): LessonInfo | undefined => LESSONS.find((l) => l.number === n);

/** Lesson 10's title follows the head the player learns with. */
export const SPECIAL_LESSON_TITLE: Record<CharacterId, string> = {
  rookie: 'HAIL MARY',
  bigtex: 'BULLDOZER',
  zorg: 'MIRAGE',
  grandpa: 'BLITZ',
  turbo: 'ROCKET BOOTS',
  dome: 'BIG HEAD',
  rumble: 'GROUND POUND',
  hopper: 'DOUBLE JUMP',
  lodestone: 'MAGNET',
};

export const lessonTitleFor = (id: LessonId, head: CharacterId): string =>
  id === 'special' ? SPECIAL_LESSON_TITLE[head] : lessonInfo(id).title;

/** The 2×2 counter square (lesson 7): which cells the player has proven. */
export type SquareCell = 'hurdle-dive' | 'truck-leap' | 'dive-truck' | 'leap-hurdle';

/**
 * What the engine publishes for the HUD every time something visible changes (`ui.tutorial`). See TutorialView in
 * ../types.ts (these fields extend it).
 */
export interface TutorialLessonView {
  lessonId: LessonId;
  lessonNumber: number;
  /** The head the player learns with (lesson 10, art). */
  head: CharacterId;
  goal: string;
  /** slate = title card · beat = playing · clear = lesson done, waiting for NEXT · complete = scrimmage done. */
  phase: 'slate' | 'beat' | 'clear' | 'complete';
  beatIndex: number;
  beatCount: number;
  beatKind: 'do' | 'demo' | 'card';
  /** The instruction, with {tokens} for keycaps. */
  say: string;
  keys: KeyPrompt[];
  now: NowPrompt | null;
  /** A hint replaces `say` while set. */
  hint: string | null;
  /** fail = a diagnosis after a failed try (amber "!"); nudge = an extra instruction, nothing failed (whistle). */
  hintKind: 'fail' | 'nudge' | null;
  /** The `fail` hint is the previous try's diagnosis and this try is live: drawn dimmed (it is not about THIS try). */
  hintStale?: boolean;
  /** Where the coach panel sits: bottom-right (default) or top-right (beats whose ball comes down bottom-right). */
  dock: 'bottom' | 'top';
  /** ✓ / ✗ / ⓘ pop: kind + an id that changes on every pop (`info` = a lesson learned that is no win: REFUSED). */
  flash: { kind: 'success' | 'fail' | 'info'; id: number } | null;
  side: 'offense' | 'defense';
  hud: HudPart[];
  /** `mark`: a threshold tick drawn on the part (0..1 of its bar), e.g. stamina's 15 = MUST BE REAL. */
  spotlight: { part: HudPart; tag: string; id: number; mark?: { at: number; label: string } }[];
  offerSkip: boolean;
  demo: boolean;
  /** Current teaching slow-mo (1 = off). */
  slowmo: number;
  /** The player has not pressed anything for a while on this beat: the bubble and the keys call for attention. */
  idle?: boolean;
  reps: number;
  count: number;
  /** Line shown after a success, until the next beat. */
  done: string | null;
  /** How `done` reads: a win (green ✓, default) or a lesson learned that is no win (amber ⓘ: "REFUSED!"). */
  doneTone?: 'win' | 'info';
  /** The ball is live (snap → whistle). The HUD's GO! badge never outlives the play. */
  live?: boolean;
  /** Game Day: the play clock is held (it waits for the player's first key): dimmed, no "snap before 0" tag. */
  clockHeld?: boolean;
  /** A success is on screen and the engine waits for it to sink in (or for the player's Action): "J NEXT". */
  holding?: boolean;
  /**
   * Coach line to play (once per id). `kind` sets how it meets the booth: an `intro` / `clear` line is a lesson's
   * headline (it cuts whatever the booth says and clears the stale queue); `praise` / `retry` are asides, dropped
   * when the booth is already talking (they used to clip the match call of the same rep: "WHAT A F—" "That's it!").
   * `alt`: a line to play instead when `line` is missing from the manifest.
   */
  voice: { line: string; id: number; kind?: VoiceKind; alt?: string } | null;
  /**
   * A lesson drill (every lesson but Game Day): the booth should leave the streak calls ("HE'S ON FIRE!!" after three
   * cones is no streak) to the real game. Read by the audio bridge (REQUEST in docs/TUTORIAL.md).
   */
  quietBooth?: boolean;
  /** Broadcast super-titles allowed now (regex source; '' = none). */
  moments: string;
  card: { kind: string } | null;
  /** Lesson 7: proven cells of the counter square. */
  square: SquareCell[] | null;
  /** Clear card: the next lesson (null after the last). */
  next: { id: LessonId; number: number; title: string } | null;
  /** The clear card is the end of Rookie Camp (lesson 5): offer a real game. */
  campClear: TutorialCamp | null;
  /** Scrimmage: score, whose drive it is, and the final card's stats. */
  scrimmage?: {
    score: [number, number];
    yards: number;
    tackles: number;
    baits: number;
    done: boolean;
    /** The drive being played: the player's (1 of 1), the CPU's (1 of 1), or both done. */
    drive: 'yours' | 'theirs' | 'done';
    /** Final result from the player's side (when done). */
    result: 'win' | 'loss' | 'tie' | null;
    /** Game Days lost before this one: the CPU eases off a notch after each loss (0–2). */
    ease: number;
  };
}

/** How a coach line meets the booth (see `TutorialLessonView.voice`). */
export type VoiceKind = 'intro' | 'clear' | 'praise' | 'retry';

/** Commands the HUD sends to the tutorial engine (clear card buttons, pause items). */
export type TutorialCommand =
  | { kind: 'next' }
  | { kind: 'replay' }
  | { kind: 'retry' }
  | { kind: 'skip' }
  | { kind: 'card' }
  | { kind: 'lesson'; id: LessonId };
